import { Folder } from 'lucide-react';
import { agentGroupRefName, type AgentAssociationRef } from '@reflex/shared';
import { useAgentListFilterActions } from '@/components/agent/agent-list-filter-context';
import { TaxonomyChip } from '@/components/agent/TaxonomyChip';
import { useRemoveAgentsFromGroup } from '@/hooks/agents/useAgentGroups';

export interface AgentGroupChipProps {
  /** The membership association: `externalId` is the group id, `data.name` its label. */
  association: AgentAssociationRef;
  /** The agent wearing the chip. Without it, "Remove from group" is not offered. */
  agentId?: string;
  /** Collapse to the icon when the strip runs out of room. */
  iconOnly?: boolean;
}

/**
 * The chip for a group membership. A folder, not a set of people: a group
 * holds runs, and the `Users` icon this started with read as "a team" — the
 * wrong noun for the thing being pointed at.
 *
 * The chrome and both actions come from {@link TaxonomyChip}; what is decided
 * here is which list filter the chip drives and which write takes the agent
 * out of the group.
 */
export function AgentGroupChip({ association, agentId, iconOnly }: AgentGroupChipProps) {
  const groupId = association.externalId;
  const filterActions = useAgentListFilterActions();
  const removeMembers = useRemoveAgentsFromGroup();
  const isActiveFilter = filterActions?.activeGroupId === groupId;

  return (
    <TaxonomyChip
      noun="Group"
      name={agentGroupRefName(association)}
      icon={Folder}
      iconOnly={iconOnly}
      isActiveFilter={isActiveFilter}
      onToggleFilter={
        filterActions
          ? () => filterActions.setGroupFilter(isActiveFilter ? null : groupId)
          : undefined
      }
      onRemove={agentId ? () => removeMembers.mutate({ groupId, agentIds: [agentId] }) : undefined}
      removeActionLabel="Remove from group"
      removePending={removeMembers.isPending}
    />
  );
}
